<template>
	<form :class="{ 'om-dim': oauthInProgress }" @submit.prevent="handleSubmit">
		<label class="omapi-integrations-form__field-wrap">
			<div class="omapi-integrations-form__field-wrap-label">
				<span>{{ provider.name }} Account Name</span>
			</div>
			<div class="omapi-integrations-form__field-wrap-field">
				<input v-model="fields.name" type="text" name="name" class="omapi-input" />
			</div>
		</label>

		<integrations-form-field
			v-for="field in visibleFields"
			:key="field.id"
			v-model="fields[field.id]"
			:field="field"
			:disabled="isLegacy"
		/>
		<input
			v-for="hiddenfield in hiddenFields"
			:key="hiddenfield.id"
			v-model="fields[hiddenfield.id]"
			type="hidden"
			:name="hiddenfield.id"
			:disabled="isLegacy"
		/>
		<!--
			Salesforce Pardot gets its own button because
			it's special and stuff.
		-->
		<div class="omapi-integrations-form__cta omapi-integrations-form__cta_pardot">
			<core-loading-button
				color="green"
				button-type="submit"
				:disabled="oauthInProgress || !fields.name || submitting"
				:loading="oauthInProgress || submitting"
			>
				{{ buttonTitle }}
			</core-loading-button>
			<core-button :disabled="oauthInProgress || submitting" @click="resetProviders"> Cancel </core-button>
		</div>
	</form>
</template>
<script>
import { createNamespacedHelpers } from 'vuex';
import { oauth } from '@/integrations/mixins/oauth';
import { form } from '@/integrations/mixins/form';
const { mapActions, mapMutations, mapState } = createNamespacedHelpers('integrations');

export default {
	mixins: [oauth, form],
	props: {
		/**
		 * Provider
		 */
		provider: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			...this.getFieldsSetup(),
			submitting: false,
			slug: 'salesforce-pardot',
			integrationId: '',
		};
	},
	computed: {
		...mapState(['addingIntegration', 'editingIntegration', 'activeContent']),
		payload() {
			return {
				id: this.integrationId,
				payload: {
					name: this.fields.name,
					providerSlug: this.slug,
					providerData: {
						business_unit_id: this.$get('fields.business_unit_id', ''),
						client_id: this.$get('fields.client_id', ''),
						client_secret: this.$get('fields.client_secret', ''),
					},
				},
			};
		},
		editing() {
			return !this.addingIntegration || this.integrationId;
		},
	},
	watch: {
		oauthComplete(complete) {
			if (complete) {
				this.refresh();
			}
		},
	},
	mounted() {
		this.$bus.$on('oauthComplete', this.updateOauthFields);
		this.$bus.$on('oauthPost', this.augmentData);

		this.integrationId = this.id;
	},
	beforeDestroy() {
		this.$bus.$off('oauthComplete', this.updateOauthFields);
		this.$bus.$off('oauthPost', this.augmentData);
	},
	methods: {
		...mapActions(['addIntegration', 'resetIntegrations', 'resetProviders', 'updateIntegration']),
		...mapMutations(['setActiveContentMessage', 'setActiveContentError']),
		/**
		 * Create Integration
		 *
		 * @since 2.4.0
		 *
		 * @returns {void}
		 */
		createIntegration() {
			return this.addIntegration(this.payload).then((body) => {
				if (!body.id || !body.providerData.oauth_authorize_url) {
					throw new Error('The oauth_authorize_url and/or integration id were not found.');
				}

				this.integrationId = body.id;
				this.fields.oauth_authorize_url = body.providerData.oauth_authorize_url;

				this.handleOauth(this.fields.oauth_authorize_url);
			});
		},
		/**
		 * Edit Integration
		 *
		 * @since 2.4.0
		 *
		 * @returns {void}
		 */
		editIntegration() {
			return this.updateIntegration(this.payload).then((body) => {
				if (!body.providerData.oauth_authorize_url) {
					throw new Error('The oauth_authorize_url was not found.');
				}

				this.handleOauth(body.providerData.oauth_authorize_url);
			});
		},
		validatePayload() {
			['name', 'client_id', 'client_secret', 'business_unit_id'].forEach((key) => {
				if (!this.$get(`fields.${key}`, '')) {
					const label =
						'name' === key
							? `${this.provider.name} Account Name`
							: this.supportFields.find((f) => f.id === key).label;
					throw new Error('Missing required field, <strong>' + label + '</strong>');
				}
			});
		},
		augmentData(payload) {
			payload.data.business_unit_id = this.$get('fields.business_unit_id', '');
			payload.data.client_id = this.$get('fields.client_id', '');
			payload.data.client_secret = this.$get('fields.client_secret', '');
			payload.data.integrationId = this.$get('fields.integrationId', '');
			payload.data.oauth_authorize_url = this.$get('fields.oauth_authorize_url', '');
		},
		/**
		 * Update field values.
		 *
		 * @param {object} params Oauth params.
		 *
		 * @returns {void}
		 */
		updateOauthFields(params) {
			Object.keys(params).map((key) => {
				if (key in this.fields) {
					this.fields[key] = params[key];
				}
			});
		},
		/**
		 * Refresh
		 *
		 * @since 2.4.0
		 *
		 * @returns {void}
		 */
		refresh() {
			const action = this.editing ? 'updated' : 'added';

			this.resetIntegrations({
				message: `Integration ${action}! Refreshing...`,
				type: 'info',
				closes: true,
			});
		},
		/**
		 * Handle Button Action
		 *
		 * @since 2.4.0
		 *
		 * @returns {void}
		 */
		handleSubmit() {
			if (this.oauthInProgress || this.submitting) {
				return;
			}

			try {
				this.validatePayload();
			} catch (error) {
				this.setActiveContentError(error);
				return;
			}

			this.submitting = true;

			const method = this.editing ? 'editIntegration' : 'createIntegration';

			this[method]()
				.catch((error) => {
					// eslint-disable-next-line no-console
					console.error({ error });
					const action = this.editing ? 'update' : 'add';
					this.setActiveContentError(`Unable to ${action} integration: ${error}`);
				})
				.finally(() => {
					this.submitting = false;
				});
		},
	},
};
</script>
